"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useAction } from "next-safe-action/hooks";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";

import { resetPostHog, trackEvent } from "@traqly/analytics/client";
import { Button } from "@traqly/ui/components/button";
import { Input } from "@traqly/ui/components/input";
import { Label } from "@traqly/ui/components/label";
import { PasswordInput } from "@traqly/ui/components/password-input";
import { Separator } from "@traqly/ui/components/separator";
import { GoogleIcon } from "@traqly/ui/icons/google-icon";
import { cn } from "@traqly/ui/lib/utils";

import { signInAction, signInWithGoogle } from "@actions/auth/sign-in.action";
import { Logo } from "@features/shared/logo";
import { loginFormSchema, type LoginFormSchema } from "@lib/schemas/auth";

export default function LoginForm() {
  const { executeAsync: signIn, isPending } = useAction(signInAction);
  const searchParams = useSearchParams();
  // Meal-prep invite token carried over from the sign-up page — a user who
  // arrived via a Shopify store install but already has an account logs in
  // instead, and the token binds that shop to their account.
  const mealPrepToken = searchParams.get("mealPrepToken") ?? "";

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormSchema>({
    defaultValues: { email: "", password: "" },
    resolver: zodResolver(loginFormSchema),
  });

  const handleGoogleClick = () => {
    // Reset first in case a previous account's identity is still persisted
    // on this device (e.g. shared browser), so this login flow doesn't get
    // attributed to the prior user.
    resetPostHog();
    trackEvent("user_logged_in", {
      auth_method: "google",
    });
  };

  async function onSubmit(values: LoginFormSchema) {
    const result = await signIn({
      ...values,
      ...(mealPrepToken ? { mealPrepToken } : {}),
    });

    if (result?.serverError) {
      return setError("password", { message: result.serverError });
    }
  }

  return (
    <div
      className={cn(
        "flex w-full max-w-[526px] shrink-0 flex-col bg-white",
        "border-mono-100 gap-5 rounded-3xl border p-6",
      )}
    >
      <div className="flex flex-col items-center gap-4">
        <Logo />

        <h2 className="text-header-h1 text-mono-900 text-center">
          Welcome back to Traqly
        </h2>

        <form
          action={signInWithGoogle}
          className="w-full"
        >
          {mealPrepToken && (
            <input
              type="hidden"
              name="mealPrepToken"
              value={mealPrepToken}
            />
          )}
          <Button
            variant="secondary"
            size="xl"
            type="submit"
            className="w-full gap-2 whitespace-nowrap [&_svg]:size-6"
            onClick={handleGoogleClick}
          >
            <GoogleIcon />
            Continue with Google
          </Button>
        </form>
      </div>

      <div className="text-body-medium-medium text-mono-700 flex items-center gap-4">
        <Separator className="flex-1" />
        or
        <Separator className="flex-1" />
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email Address</Label>

          <Input
            {...register("email")}
            id="email"
            inputSize="lg"
            type="email"
            placeholder="john@example.com"
            error={errors.email?.message}
            autoComplete="email"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="password">Password</Label>

          <PasswordInput
            {...register("password")}
            id="password"
            inputSize="lg"
            placeholder="Password"
            error={errors.password?.message}
            autoComplete="current-password"
          />

          <div className="flex justify-end">
            <Link
              href="/forgot-password"
              className="text-body-large-medium text-blue-700 underline"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        <Button
          variant="primary"
          size="xl"
          type="submit"
          className="w-full"
          isLoading={isPending}
        >
          Log In
        </Button>
      </form>

      <div className="flex items-center justify-center">
        <span className="text-body-large-regular text-mono-700">
          Don&apos;t have an account?
        </span>

        <Link href="/sign-up">
          <Button
            variant="tertiary"
            size="lg"
            type="button"
          >
            Sign Up
          </Button>
        </Link>
      </div>
    </div>
  );
}
